import {
  normalizeDiscoveryRequest,
  buildDiscoveryPath,
  buildGenericEntryReplacement,
  COMUNE_PRESET_RADIUS_KM,
  ESPLORA_RESERVED_SEGMENT,
  RADIUS_KM_MIN,
  RADIUS_KM_MAX,
  categoryById,
  categoryIdToQuerySlug,
  type DiscoveryDescriptor,
  type DiscoveryRoute,
  type MapBbox,
} from '@buzzmap/shared';

/**
 * Filter-patch → canonical URL (T012-FE, canonical territories plan).
 *
 * The URL is the v2 authority for the public filters (plan l.60): every
 * public filter action navigates to the next canonical URL instead of
 * mutating local state. The shared codec (`normalizeDiscoveryRequest`) is
 * the ONLY serializer — a patch is applied to the current descriptor's
 * canonical query, the result is re-normalized, and the caller navigates to
 * the canonical form (or the one-hop redirect target). No serialization
 * rule is duplicated here: ordering, dedup, percent-encoding, path-category
 * precedence and tracking/unknown preservation all stay in `@buzzmap/shared`.
 *
 * Route movement on a category change (plan l.58):
 * - exactly one SEO category on a territory → the category path;
 * - zero or 2+ categories (or `altro`, which has no category page) → the
 *   territorial Esplora surface (`{territory}/esplora`) — zero categories
 *   stays Esplora, NEVER Home, and the rest of the query (dates, radius,
 *   search, view/mapBbox, tracking) survives untouched;
 * - generic `/` or `/esplora` keeps its path (a single SEO category stays in
 *   the query on a generic surface, per the codec).
 */

export interface DiscoveryFilterPatch {
  /** Replace the category selection (DB ids). Empty array = clear. */
  categoryIds?: string[];
  /** Calendar YYYY-MM-DD; `null` clears the whole date range. */
  startDate?: string | null;
  /** Calendar YYYY-MM-DD; defaults to startDate (same-day) when omitted. */
  endDate?: string | null;
  /**
   * Move the date axis onto a PERIOD page (SEO wave 3 vocabulary: `oggi`,
   * `questo-weekend`, one of the twelve months) instead of a date query.
   *
   * Where a period-in-path shape EXISTS (territory Home → the period path)
   * the route becomes `{territory}/{slug}`; where it does not — category and
   * Esplora surfaces, and the codec's "category > period" precedence — the
   * period materializes as `?quando={slug}` on the current route, which is
   * exactly what a period carried on a category URL already normalizes to.
   * `startDate`/`endDate` are dropped with it: dates and period are the same
   * window axis (the period wins in `EventsContext` anyway), so keeping both
   * would show one window and query another.
   *
   * Callers must only ask for it when the server said the territory HAS
   * period pages (`resolution.periodLinks` non-empty) and the current route
   * is territorial — elsewhere the patch still builds a valid URL, but the
   * window would not be applied by any surface.
   */
  periodSlug?: string;

  /** Free-text query; `null`/'' clears it. */
  search?: string | null;
  /** Explicit radius in km; `null` clears it (preset then applies). */
  radiusKm?: number | null;
  /**
   * Move to the Esplora surface of the current territory before applying the
   * patch (T013 tab/CTA navigation). A path category becomes `?categorie=` so
   * the selection survives the surface move; the rest of the canonical query
   * (filters/radius/view/mapBbox/tracking) is preserved untouched (plan l.64).
   */
  surface?: 'esplora';
  /**
   * Map presentation (T014-FE): `'map'` materializes `view=map` (the codec
   * moves a Home surface to Esplora on its own); `'list'` removes the `view`
   * key. Orthogonal to `mapBbox` — a desktop split view legitimately carries
   * a bbox without `view=map`, so the two are never forced in lockstep.
   */
  view?: 'list' | 'map';
  /** Explicit viewport, west,south,east,north; `null` clears it (plan l.65-66). */
  mapBbox?: MapBbox | null;
}

/** Members-equal comparison — a reordered selection is the same filter. */
export function sameIdSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

function routeAfterCategoryPatch(route: DiscoveryRoute, ids: string[]): DiscoveryRoute {
  if (route.kind !== 'territory-home' && route.kind !== 'territory-esplora' && route.kind !== 'territory-category') {
    return route; // generic surface keeps its path
  }
  // Clearing filters on a territory Home stays Home (filters belong to
  // Esplora; nothing on Home produces them today).
  if (route.kind === 'territory-home' && ids.length === 0) return route;
  if (ids.length === 1) {
    const first = ids[0];
    const entry = first !== undefined ? categoryById(first) : undefined;
    if (entry) {
      return { kind: 'territory-category', segments: route.segments, categorySlug: entry.slug };
    }
  }
  return { kind: 'territory-esplora', segments: route.segments };
}

/** Same territory/generic surface moved onto Esplora (T013 tab/CTA navigation). */
function esploraSurfaceRoute(route: DiscoveryRoute): DiscoveryRoute {
  if (route.kind === 'territory-home' || route.kind === 'territory-category') {
    return { kind: 'territory-esplora', segments: route.segments };
  }
  if (route.kind === 'territory-period') {
    // No period × esplora path exists — the period demotes to ?quando= (the
    // caller re-materializes it onto the target below).
    return { kind: 'territory-esplora', segments: route.segments };
  }
  if (route.kind === 'generic-home') return { kind: 'generic-esplora' };
  return route; // already an Esplora surface
}

/** The canonical query with `patch` applied on top of `d.canonicalSearch`. */
function paramsAfterPatch(
  d: DiscoveryDescriptor,
  patch: DiscoveryFilterPatch,
  nextRoute: DiscoveryDescriptor['route']
): URLSearchParams {
  const params = new URLSearchParams(
    d.canonicalSearch.startsWith('?') ? d.canonicalSearch.slice(1) : d.canonicalSearch
  );

  // A path category becomes a query selection when the surface moves off the
  // category path (Esplora tab / weekend CTA from a category page) — the
  // selection survives the surface move (plan l.64). A `categoryIds` patch
  // below replaces the selection anyway, so it skips this carry.
  if (
    patch.surface === 'esplora' &&
    patch.categoryIds === undefined &&
    d.categorySource === 'path' &&
    d.categoryIds.length > 0 &&
    nextRoute.kind === 'territory-esplora'
  ) {
    const pathSlugs = d.categoryIds
      .map((id) => categoryIdToQuerySlug(id))
      .filter((s): s is string => s !== undefined);
    if (pathSlugs.length > 0) params.set('categorie', pathSlugs.join(','));
  }

  if (patch.categoryIds !== undefined) {
    const slugs = patch.categoryIds
      .map((id) => categoryIdToQuerySlug(id))
      .filter((s): s is string => s !== undefined);
    if (slugs.length === 0) {
      params.delete('categorie');
    } else if (nextRoute.kind === 'territory-category') {
      // The single category lives in the path — the codec would strip the
      // query key anyway (path wins), so don't emit it.
      params.delete('categorie');
    } else {
      params.set('categorie', slugs.join(','));
    }
  }

  if (patch.startDate === null) {
    params.delete('startDate');
    params.delete('endDate');
  } else if (patch.startDate !== undefined) {
    params.set('startDate', patch.startDate);
    if (patch.endDate) params.set('endDate', patch.endDate);
    else if (patch.endDate === null) params.delete('endDate');
    else params.set('endDate', patch.startDate);
  } else if (patch.endDate === null) {
    params.delete('endDate');
  } else if (patch.endDate !== undefined) {
    params.set('endDate', patch.endDate);
  }

  if (patch.search === null || patch.search === '') {
    params.delete('search');
  } else if (patch.search !== undefined) {
    params.set('search', patch.search);
  }

  if (patch.radiusKm === null) {
    params.delete('radiusKm');
  } else if (patch.radiusKm !== undefined) {
    params.set('radiusKm', String(patch.radiusKm));
  }

  if (patch.view === 'map') {
    params.set('view', 'map');
  } else if (patch.view === 'list') {
    params.delete('view');
  }

  if (patch.mapBbox === null) {
    params.delete('mapBbox');
  } else if (patch.mapBbox !== undefined) {
    const { west, south, east, north } = patch.mapBbox;
    params.set('mapBbox', `${west},${south},${east},${north}`);
  }

  return params;
}

/**
 * Next canonical URL for `{pathname, search}` with `patch` applied, or
 * `null` when the result would not normalize (invalid input — the caller
 * must NOT navigate, so a bad patch can never broaden the query).
 */
export function nextDiscoveryUrl(
  current: { pathname: string; search: string },
  patch: DiscoveryFilterPatch
): string | null {
  const norm = normalizeDiscoveryRequest({ pathname: current.pathname, search: current.search });
  if (norm.kind !== 'canonical' && norm.kind !== 'redirect') return null;
  const d = norm.descriptor;

  // Period in the path of the CURRENT url — re-materialized as ?quando= below
  // whenever the patch moves off the period path (upstream T028: leaving a
  // period path for a category path moves the period into the query).
  const periodFromPath = d.route.kind === 'territory-period' ? d.route.periodSlug : undefined;

  let nextRoute = d.route;
  if (patch.surface === 'esplora') nextRoute = esploraSurfaceRoute(nextRoute);
  if (patch.categoryIds !== undefined) {
    nextRoute = routeAfterCategoryPatch(nextRoute, patch.categoryIds);
  }
  // An explicit date patch REPLACES the preset window: dates and a period are
  // the same axis, so the patch leaves the period path for its plain Home
  // (the codec keeps both on a hand-edited URL — the server intersects — but
  // a user action must land on the window it asked for, not an intersection).
  const setsDates = patch.startDate !== undefined || patch.endDate !== undefined;
  if (setsDates && nextRoute.kind === 'territory-period') {
    nextRoute = { kind: 'territory-home', segments: nextRoute.segments };
  }
  // A period patch owns the window: it replaces any date pair (same axis) and
  // materializes where a period-in-path shape exists — Home becomes the period
  // path, an existing period path swaps its slug. Category/Esplora/generic
  // routes keep their path and take the period as `?quando=` below (upstream
  // R5/C4: no category × period and no esplora × period path exist).
  const periodSlug = patch.periodSlug;
  if (periodSlug !== undefined) {
    if (nextRoute.kind === 'territory-home') {
      nextRoute = { kind: 'territory-period', segments: nextRoute.segments, periodSlug };
    } else if (nextRoute.kind === 'territory-period') {
      nextRoute = { ...nextRoute, periodSlug };
    }
  }
  const params = paramsAfterPatch(d, patch, nextRoute);
  if (periodSlug !== undefined) {
    params.delete('startDate');
    params.delete('endDate');
    if (nextRoute.kind === 'territory-period') params.delete('quando');
    else params.set('quando', periodSlug);
  } else if (setsDates) {
    // Same rule on every surface: an explicit date patch replaces the preset
    // window, so a query period must go too (otherwise the period would keep
    // winning over the dates the user just picked — see EventsContext).
    params.delete('quando');
  } else if (periodFromPath !== undefined && nextRoute.kind !== 'territory-period') {
    params.set('quando', periodFromPath);
  }

  const next = normalizeDiscoveryRequest({
    pathname: buildDiscoveryPath(nextRoute),
    search: params.toString(),
  });
  if (next.kind === 'canonical') return next.descriptor.canonicalUrl;
  if (next.kind === 'redirect') return next.target;
  return null;
}

/**
 * Canonical URL for a Home link from the current URL (T013, plan l.64):
 * the territorial Home of the same territory (generic Home when the current
 * URL has no territory), with ALL filter and map presentation state dropped —
 * category browsing lives on Esplora (plan l.58) and "a Home link drops
 * map-only presentation parameters". Recognized tracking and unknown params
 * survive (plan l.60). Returns `null` for a non-discovery path or an invalid
 * URL — the caller falls back to `/`, which the v2 entry gate central-
 * normalizes like any other generic entry.
 */
export function discoveryHomeUrl(current: { pathname: string; search: string }): string | null {
  const norm = normalizeDiscoveryRequest({ pathname: current.pathname, search: current.search });
  if (norm.kind !== 'canonical' && norm.kind !== 'redirect') return null;
  const d = norm.descriptor;

  const route: DiscoveryRoute =
    d.route.kind === 'territory-home' ||
    d.route.kind === 'territory-esplora' ||
    d.route.kind === 'territory-category' ||
    d.route.kind === 'territory-period'
      ? { kind: 'territory-home', segments: d.route.segments }
      : { kind: 'generic-home' };

  const params = new URLSearchParams();
  for (const [key, value] of d.trackingParams) params.append(key, value);
  for (const [key, value] of d.unknownParams) params.append(key, value);

  const next = normalizeDiscoveryRequest({
    pathname: buildDiscoveryPath(route),
    search: params.toString(),
  });
  if (next.kind === 'canonical') return next.descriptor.canonicalUrl;
  if (next.kind === 'redirect') return next.target;
  return null;
}

/**
 * clearFilters semantics (plan l.142): categories, dates and search go;
 * locality (the territory path) and radius stay; view/mapBbox and
 * tracking/unknown params survive (they are presentation/attribution, not
 * the filters this action clears).
 */
export function clearFiltersUrl(current: { pathname: string; search: string }): string | null {
  return nextDiscoveryUrl(current, {
    categoryIds: [],
    startDate: null,
    endDate: null,
    search: null,
  });
}

/**
 * Builds the territorial replacement target, carrying any non-geo filters
 * (`carrySearch` = the descriptor's `canonicalSearch`) so a geo-hint entry keeps
 * its categorie/search/dates (B-1). A bare entry (empty carry) delegates to the
 * shared codec so the radius materialization rule stays in one place.
 */
export function composeTerritoryTarget(
  entry: 'home' | 'esplora',
  comunePath: string,
  carrySearch: string,
  savedRadiusKm: number | null,
  presetRadiusKm: number
): string {
  if (!carrySearch) {
    return buildGenericEntryReplacement({
      entry,
      savedComunePath: comunePath,
      savedRadiusKm,
      presetRadiusKm,
    });
  }
  const base = entry === 'esplora' ? `${comunePath}/${ESPLORA_RESERVED_SEGMENT}` : comunePath;
  const hasRadius = /[?&]radiusKm=/.test(carrySearch);
  const shouldCarryRadius =
    !hasRadius &&
    savedRadiusKm !== null &&
    savedRadiusKm >= RADIUS_KM_MIN &&
    savedRadiusKm <= RADIUS_KM_MAX &&
    savedRadiusKm !== presetRadiusKm;
  const radius = shouldCarryRadius ? `${carrySearch ? '&' : '?'}radiusKm=${savedRadiusKm}` : '';
  return `${base}${carrySearch}${radius}`;
}

/**
 * Territorial URL for a comune the user EXPLICITLY picked in the Località
 * sheet (v2): the new comune becomes the active territory immediately.
 *
 * Contract (docs/TERRITORIAL_NAVIGATION.md §2-4): a deliberate territory change
 * resets filters exactly like a Home link or "Tutta Italia" — categories,
 * dates, search, period and the stale `mapBbox` (it describes the OLD area)
 * are dropped. What survives: the surface (an Esplora or `view=map` URL stays
 * on `{comune}/esplora`, anything else lands on the comune Home), `view=map`
 * itself (presentation, not a filter), and tracking/unknown params (plan l.60).
 * The radius follows the codec rule shared with the entry replacement:
 * materialized as `?radiusKm=` only when it differs from the comune preset.
 *
 * Returns `null` when the current URL is not a discovery URL (profile, organizer
 * console, `/event/:id`…) or the comune path does not normalize: the caller then
 * keeps the preference but must not navigate.
 */
export function territoryPickUrl(
  current: { pathname: string; search: string },
  comunePath: string,
  radiusKm: number | null
): string | null {
  const norm = normalizeDiscoveryRequest({ pathname: current.pathname, search: current.search });
  if (norm.kind !== 'canonical' && norm.kind !== 'redirect') return null;
  const d = norm.descriptor;

  const onEsplora =
    d.route.kind === 'generic-esplora' ||
    d.route.kind === 'territory-esplora' ||
    d.view === 'map';
  const carry = new URLSearchParams();
  if (d.view === 'map') carry.set('view', 'map');
  for (const [key, value] of d.trackingParams) carry.append(key, value);
  for (const [key, value] of d.unknownParams) carry.append(key, value);
  const carrySearch = carry.toString();

  const target = composeTerritoryTarget(
    onEsplora ? 'esplora' : 'home',
    comunePath,
    carrySearch ? `?${carrySearch}` : '',
    radiusKm,
    COMUNE_PRESET_RADIUS_KM
  );
  const url = new URL(target, 'http://x');
  const next = normalizeDiscoveryRequest({ pathname: url.pathname, search: url.search });
  if (next.kind === 'canonical') return next.descriptor.canonicalUrl;
  if (next.kind === 'redirect') return next.target;
  return null;
}
